/* eslint-disable max-len */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import axios, { API_URL } from '../services/axios';
import { MortgageErrorStatus, MortgageProductResult } from '../types/mortgages';
import { Broker, Property } from '../types/property';
import floatToCurrency, { parseNumber } from '../utils/floatToCurrency';
import {
  getDefaultDeposit,
  getDefaultTerm,
  getDepositPercentage,
} from '../utils/property/getMortgageCalculatorDefaults';
import trackEvents from '../utils/trackEvents';
import useApi from './useApi';
import useDebounce from './useDebounce';

export interface MortgageCalculatorFormValues {
  paymentTerm: number;
  propertyValue?: number;
  deposit: number;
  interestRate?: number;
}

export interface MortgageEvent {
  value: number;
  deposit: number;
  term: number;
  fieldModified?: string;
  depositFromCookie: boolean;
  termFromCookie: boolean;
}

export interface MortgageCalcData {
  product: Product | null;
  data: MortgageProductResult | undefined;
  error: any;
  loading: boolean;
  sending: boolean;
  errorStatus?: MortgageErrorStatus;
  hasResult: boolean;
  depositPercentage?: number;
  hasMortgageCalculator: boolean;
  hasBrokerCalculator: boolean;
  getMortgageResult: (values: MortgageCalculatorFormValues) => void;
  logMortgageEvent: (event: MortgageEvent) => void;
  submitLead: (values: any) => Promise<boolean>;
  broker: Broker | null;
}

const COMMENT = 'Please confirm my Mortgage Eligibility';

const DEBOUNCE_TIME = process?.env?.JEST_WORKER_ID ? 0 : 1000;

interface Product {
  deposit: number;
  propertyValue: number;
  interestRate: number | undefined;
  monthlyPayments: string;
}

const getError = (depositPercentage?: number, data?: MortgageProductResult): MortgageErrorStatus | undefined => {
  let eStatus: MortgageErrorStatus | undefined;

  if (data) {
    const { deposit, value, product } = data;

    if (deposit === value) {
      eStatus = { error: 'DEPOSIT_EQUAL_VALUE' };
    } else if (deposit > value) {
      eStatus = { error: 'DEPOSIT_GREATER_VALUE' };
    } else if (depositPercentage && depositPercentage > 99) {
      eStatus = { error: 'INVALID_LTV' };
    } else if (product == null) {
      eStatus = { error: 'NO_RESULTS' };
    }
  }

  return eStatus;
};

export const getBroker = (property: Property): Broker | null => {
  const { brokers, agents } = property;

  if (brokers && property.saleType?.key !== 'rent') {
    // If any agent has no brokers, return none
    if (agents && brokers.length < agents.length) {
      return null;
    }

    if (brokers.length === 1) {
      return brokers[0];
    }

    // If all brokers are the same, return the first one
    if (brokers.length > 1) {
      const firstBroker = brokers[0].name;
      const allBrokers = brokers.filter((b) => b.name === firstBroker);

      if (allBrokers.length === brokers.length) {
        return brokers[0];
      }
    }
  }

  return null;
};

const postBrokerLead = async (propertyId: number, brokerId: number, dispatch: Dispatch, data: any) => {
  try {
    await axios(
      {
        method: 'POST',
        url: `${API_URL}/property/${propertyId}/lead/broker/${brokerId}`,
        data,
      },
      dispatch,
    );

    return true;
  } catch {
    return false;
  }
};

const useMortgageCalculator = (property: Property): MortgageCalcData => {
  const dispatch = useDispatch();
  const noOfInteractions = useRef(0);
  const [product, setProduct] = useState<Product | null>(null);
  const [data, error, loading, fetchProduct] = useApi<MortgageProductResult>('', false);
  const [sending, setSending] = useState(false);
  const depositPercentage = product ? getDepositPercentage(product.deposit, product.propertyValue) : undefined;
  const errorStatus = getError(depositPercentage, data);
  const hasResult = !errorStatus && !!data;
  const broker = getBroker(property);
  const calcModeRef = useRef<'api' | 'manual'>('api');
  const hasBrokerCalculator = !!(property.brokers && property.brokers.length > 0 && property.published);
  const hasMortgageCalculator =
    property.type === 'property' &&
    property.propertyType?.key === 'residential' &&
    property.saleType?.key === 'sale' &&
    property.countryCode === 'GBR' &&
    property.price !== undefined &&
    (typeof property.price.price === 'number' || typeof property.price.minPrice === 'number') &&
    property.price.priceType !== 'PUBLIC_AUCTIONS' &&
    property.status?.key !== 'sold' &&
    property.status?.key !== 'saleAgreed' &&
    !!property.published &&
    !(property.cashSaleOnly || property.price.priceType === 'CASH_OFFERS_ONLY');

  const debounceFetchResult = useDebounce(
    fetchProduct,
    DEBOUNCE_TIME,
    {
      leading: false,
      trailing: true,
    },
    [],
  );

  const calculateMonthly = (values: MortgageCalculatorFormValues) => {
    const { propertyValue, deposit, interestRate, paymentTerm } = values;

    if (!propertyValue || deposit >= propertyValue) {
      setProduct({
        monthlyPayments: '-',
        deposit,
        interestRate: interestRate != null ? interestRate : undefined,
        propertyValue: propertyValue != null ? propertyValue : 0,
      });
    } else if (interestRate && propertyValue) {
      const loanAmount = (propertyValue || 0) - deposit;
      const monthlyInterestRate = interestRate / 100 / 12;
      const totalPayments = paymentTerm * 12;

      if (monthlyInterestRate === 0) {
        setProduct({
          monthlyPayments: floatToCurrency(loanAmount / totalPayments),
          deposit,
          interestRate,
          propertyValue,
        });
      } else {
        const numerator = monthlyInterestRate * (1 + monthlyInterestRate) ** totalPayments;
        const denominator = (1 + monthlyInterestRate) ** totalPayments - 1;

        const monthlyPayments = floatToCurrency(loanAmount * (numerator / denominator));

        setProduct({ monthlyPayments, deposit, interestRate, propertyValue });
      }
    }
  };

  const getMortgageResult: MortgageCalcData['getMortgageResult'] = (values) => {
    // If user has edited the interest rate away from the default rate
    // calculate mortgage payment without product request
    if (values.interestRate) {
      calcModeRef.current = 'manual';
      calculateMonthly(values);
    } else {
      calcModeRef.current = 'api';
      let url = `/mortgages/mortgage/product?value=${values.propertyValue}`;
      url += `&term=${values.paymentTerm}&defaultTerm=${getDefaultTerm(property)}`;
      url += `&deposit=${values.deposit}&defaultDeposit=${getDefaultDeposit(property)}`;

      if (property.development) {
        url += '&newHome=true';
      }

      debounceFetchResult(url);
    }
  };

  const submitLead: MortgageCalcData['submitLead'] = async (values) => {
    let res = false;
    setSending(true);

    const mValues = {
      ...values,
      propertyValue: parseNumber(values.propertyValue),
      deposit: parseNumber(values.deposit),
      paymentTerm: parseNumber(values.paymentTerm),
    };

    if (broker?.asssociatedAgents?.length) {
      const results = [];
      for (let i = 0; i < broker.asssociatedAgents.length; i += 1) {
        const mData = {
          ...mValues,
          interestRate: data?.product?.initialRate,
          contact: broker.asssociatedAgents[i],
          comment: COMMENT,
        };
        mData.contact = broker.asssociatedAgents[i];

        results.push(postBrokerLead(property.id, broker.id, dispatch, mData));
      }

      res = (await Promise.all(results))[0];
    } else if (broker) {
      const mData = {
        ...mValues,
        interestRate: data?.product?.initialRate,
        contact: property.account.accountNumber,
        comment: COMMENT,
      };

      res = await postBrokerLead(property.id, broker.id, dispatch, mData);
    }

    setSending(false);
    return res;
  };

  const logMortgageEvent = async (payload: MortgageEvent) => {
    noOfInteractions.current += 1;

    trackEvents({
      property: property.id,
      eventType: 'MORTGAGE_CALCULATOR_INTERACTION',
      eventPageType: property.type === 'development' ? 'DEVELOPMENT_DETAILS' : 'PROPERTY_DETAILS',
      parameters: {
        sequence: noOfInteractions.current,
        outcome: data?.product ? 'Result' : 'Error',
        ...payload,
        deposit: parseNumber(payload.deposit),
        term: parseNumber(payload.term),
      },
    });
  };

  useEffect(() => {
    if (calcModeRef.current === 'api' && data?.product?.initialMonthlyPayment) {
      setProduct({
        monthlyPayments: floatToCurrency(data.product?.initialMonthlyPayment),
        deposit: data.deposit,
        propertyValue: data.value,
        interestRate: data.product.initialRate,
      });
    }
  }, [data]);

  return {
    data,
    product,
    error,
    loading,
    getMortgageResult,
    depositPercentage,
    errorStatus,
    hasResult,
    hasMortgageCalculator,
    hasBrokerCalculator,
    logMortgageEvent,
    submitLead,
    sending,
    broker,
  };
};

export default useMortgageCalculator;
